import {
  mailPolicySchema,
  mailTemplateSchema,
  mailTemplateTestRequestSchema,
  mailTemplateTestResponseSchema,
  mailTemplatesResponseSchema,
  updateMailPolicyRequestSchema,
  updateMailTemplateRequestSchema,
  type MailPolicy,
  type MailTemplate,
  type MailTemplateTestRequest,
  type NotificationEventKey,
  type UpdateMailPolicyRequest,
  type UpdateMailTemplateRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';

export const mailPolicyQueryKey = ['settings', 'mail-policy'] as const;
export const mailTemplatesQueryKey = ['settings', 'mail-templates'] as const;

export function useMailPolicy(enabled = true) {
  return useQuery({
    queryKey: mailPolicyQueryKey,
    queryFn: () => apiFetch('/settings/mail-policy', mailPolicySchema),
    enabled,
  });
}

export function useUpdateMailPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMailPolicyRequest) =>
      apiFetch('/settings/mail-policy', mailPolicySchema, {
        method: 'PATCH',
        body: JSON.stringify(updateMailPolicyRequestSchema.parse(body)),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(mailPolicyQueryKey, data);
    },
  });
}

export function useMailTemplates(enabled = true) {
  return useQuery({
    queryKey: mailTemplatesQueryKey,
    queryFn: () => apiFetch('/settings/mail-templates', mailTemplatesResponseSchema),
    enabled,
  });
}

export function useUpdateMailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventKey,
      body,
    }: {
      eventKey: NotificationEventKey;
      body: UpdateMailTemplateRequest;
    }) =>
      apiFetch(`/settings/mail-templates/${encodeURIComponent(eventKey)}`, mailTemplateSchema, {
        method: 'PATCH',
        body: JSON.stringify(updateMailTemplateRequestSchema.parse(body)),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: mailTemplatesQueryKey });
    },
  });
}

export function useResetMailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventKey: NotificationEventKey) =>
      apiFetch(
        `/settings/mail-templates/${encodeURIComponent(eventKey)}/reset`,
        mailTemplateSchema,
        { method: 'POST' },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: mailTemplatesQueryKey });
    },
  });
}

export function useSendMailTemplateTest() {
  return useMutation({
    mutationFn: (body: MailTemplateTestRequest) =>
      apiFetch('/settings/mail-templates/test', mailTemplateTestResponseSchema, {
        method: 'POST',
        body: JSON.stringify(mailTemplateTestRequestSchema.parse(body)),
      }),
  });
}

export type { MailPolicy, MailTemplate };
